<script setup lang="ts">
import SVGIcon from 'frontcore/views/icons/SVGIcon.vue'
import {
  SegmentedButtonItem,
  SegmentedButtonItemStyle,
} from 'frontcore/views/segmented-button/types'

type Props = {
  items: SegmentedButtonItem[]
  value: number,
}
const props = defineProps<Props>()

type Emits = (e: 'input', id: Props['value']) => void
const emit = defineEmits<Emits>()

const onClick = (item: SegmentedButtonItem) => {
  if (item.disabled) { return }
  emit('input', item.id)
}

const defaultItemStyle: SegmentedButtonItemStyle = {
  unselected: {
    icon: 'fill-core-primary-30',
    button: 'hover:bg-core-primary-95 active:bg-core-primary-90',
  },
  selected: {
    icon: 'fill-core-primary-30',
    button: 'bg-core-primary-80 hover:bg-core-primary-70 active:bg-core-primary-60',
  },
}

const itemStyle = (item: SegmentedButtonItem) => {
  const selected = item.id === props.value
  const style = { ...(item.style ?? defaultItemStyle) }
  const selectedStyle = selected ? style.selected : style.unselected

  let button: (Record<string, boolean> | string)[] = [
    {'segmented-button__item--selected': selected, 'segmented-button__item--disabled': item?.disabled || false },
  ]
  if (!item.disabled && selectedStyle.button) {
    button.push(selectedStyle.button)
  }

  if (selected && item.disabled && style.selected.disabledButton) {
    button = [style.selected.disabledButton]
  }

  return {
    button,
    icon: selectedStyle.icon ?? '',
  }
}
</script>

<template>
  <div class="segmented-button tw-flex">
    <button
      v-for="item in items"
      :key="item.id"
      :class="[
        'segmented-button__item',
        'tw-flex tw-gap-1 tw-items-center tw-justify-center',
        'tw-px-4 tw-py-2',
        itemStyle(item).button,
      ]"
      :data-test="`segmented-button-item-${item.id}`"
      @click="() => onClick(item)">
      <SVGIcon
        v-if="item.iconName"
        :colorClass="itemStyle(item).icon"
        :iconName="item.iconName"
        :remSize="1.25" />
      <span
        v-if="item.label"
        :data-test="`segmented-button-item-${item.id}-label`"
        :class="[
          'segmented-button__item__label',
          'buttons--button-01',
          item.iconName ? 'tw-hidden sm:tw-flex' : ''
        ]">
        {{ item.label }}
      </span>
    </button>
  </div>
</template>

<style scoped>
.segmented-button {
  --border-width: 1px;
}

.segmented-button > :not([hidden]) {
  border: var(--border-width) solid var(--core-neutral-cold-80);
}

.segmented-button > :not([hidden]) ~ :not([hidden]) {
  border-right-width: var(--border-width);
  border-left-width: 0;
}

.segmented-button > :not([hidden]):first-child {
  border-bottom-left-radius: var(--s-1);
  border-top-left-radius: var(--s-1);
}

.segmented-button > :not([hidden]):last-child {
  border-bottom-right-radius: var(--s-1);
  border-top-right-radius: var(--s-1);
}

.segmented-button__item {
  cursor: pointer;
}

.segmented-button__item.segmented-button__item--disabled {
  cursor: not-allowed;
  background: var(--core-neutral-cold-95);
}

.segmented-button__item:not(.segmented-button__item--disabled):focus-visible {
  position: relative;
  z-index: 1;
  outline: 3px solid var(--auxiliary-info-60) !important;
  border-radius: 0.25rem;
  box-shadow:
    inset 0 0 0 1px var(--auxiliary-info-100),
    0 0 4px 0 var(--auxiliary-info-70);
}

.segmented-button__item--selected.segmented-button__item--disabled {
  background: var(--core-neutral-cold-60);
}

.segmented-button__item--disabled .segmented-button__item__label {
  color: var(--core-neutral-cold-50);
}

.segmented-button__item--disabled svg {
  fill: var(--core-neutral-cold-50);
}

.segmented-button__item--selected.segmented-button__item--disabled .segmented-button__item__label {
  color: var(--core-neutral-cold-100);
}

.segmented-button__item--selected.segmented-button__item--disabled svg {
  fill: var(--core-neutral-cold-100);
}
</style>
